import React, { createContext, useContext, useEffect } from 'react';

import api from '../services/api';
import { listWorkspaces } from '../services/workspace/api';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import {
  authenticate,
  confirmEmail,
  getPartner,
  getPermissions,
  getUser,
  register,
  resetPassword,
  updatePartner,
  updateUser,
} from '@/services/auth/api';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useLocalStorage('@App:token', null);
  const [partner, setPartner] = useLocalStorage('@App:partner', null);
  const [user, setUser] = useLocalStorage('@App:user', null);
  const [permissions, setPermissions] = useLocalStorage('@App:permissions', []);
  const [workspaces, setWorkspaces] = useLocalStorage('@App:workspaces', []);
  const [currentWorkspace, setCurrentWorkspace] = useLocalStorage(
    '@App:workspaceId',
    null,
  );

  api.defaults.headers.common['x-access-token'] = token;

  const signed = Boolean(token);

  async function login(payload) {
    return authenticate(payload).then(response => {
      setToken(response.token);
      setWorkspaces(response.workspaces);
      const workspace = currentWorkspace
        ? response.workspaces.find(
            workspace => workspace.external_id == currentWorkspace,
          )
        : null;

      setCurrentWorkspace(
        workspace ? currentWorkspace : response.workspaces[0].external_id,
      );
    });
  }

  async function refreshUser() {
    await getPermissions().then(permissions => {
      setPermissions(permissions);
    });
    await getPartner().then(setPartner);
    await getUser().then(setUser);
    await ListWorkspaces();
  }

  function logout() {
    setToken(null);
    setPartner(null);
    setUser(null);
    setWorkspaces(null);
  }

  async function UpdateUser(payload) {
    await updateUser(payload).then(setUser);
  }

  async function UpdatePartner(payload) {
    await updatePartner(payload).then(setPartner);
  }

  async function ConfirmEmail(payload) {
    await confirmEmail(payload).then(({ token }) => {
      setToken(token);
    });
  }

  async function Register(userData) {
    let signed = false;

    await register(userData)
      .then((data) => {
        if (data.token) {
          api.defaults.headers.common['x-access-token'] = data.token;
          setToken(data.token);
          signed = true;
        }
      });

    if (signed) {
        await ListWorkspaces();
        await refreshUser();
    }
    
    return signed;
  }

  async function ResetPassword(payload) {
    return resetPassword(payload).then((response) => {
      if (response.token) {
        setToken(response.token);
      }
      return response;
    });
  }

  async function ListWorkspaces() {
    await listWorkspaces().then(({ workspaces }) => {
      setWorkspaces(workspaces);
      const workspace = currentWorkspace
        ? workspaces.find(
            workspace => workspace.external_id == currentWorkspace,
          )
        : null;

      setCurrentWorkspace(
        workspace ? currentWorkspace : workspaces[0].external_id,
      );
    });
  }

  useEffect(() => {
    if (token) refreshUser();
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        signed,
        partner,
        user,
        permissions,
        workspaces,
        setWorkspaces,
        currentWorkspace,
        setCurrentWorkspace,
        login,
        logout,
        UpdateUser,
        UpdatePartner,
        ResetPassword,
        ConfirmEmail,
        refreshUser,
        ListWorkspaces,
        Register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
